import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import {
	Alert,
	Button,
	CollapsibleSection,
	cn,
	EditableTagField,
	EmptyState,
	FiltersIcon,
	Input,
	Modal,
	Pagination,
	QuestionIcon,
	Radio,
	SafeHtmlRenderer,
	Select,
	Spinner,
	type Step,
	Steps,
	Table,
	type TableColumn,
	Text,
	TextArea,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	WarningIcon,
} from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { CycleManualEmailCreateInput, CycleManualEmailRecipientList } from '../../types'

const EXCLUSION_REASON_LABELS: Record<string, string> = {
	'globally-excluded': 'Excluded from all cycle emails',
	'population-rule': 'Not in the selected population',
	'task-filter': 'No tasks matching the selected filters',
}

const TASK_STATUS_OPTIONS = [
	{ value: 'not-started', label: 'Not Started' },
	{ value: 'in-progress', label: 'In Progress' },
	{ value: 'completed', label: 'Completed' },
	{ value: 'declined', label: 'Declined' },
	{ value: 'rejected', label: 'Rejected' },
] as const

interface CycleManualEmailFormProps {
	cycleId: string
	organizationId: string
	onSubmit: (data: CycleManualEmailCreateInput) => void
	loading: boolean
}

const steps: (Step & { id: string })[] = [
	{ label: 'Select Email Template', id: 'select-email-template' },
	{ label: 'Recipients', id: 'recipients' },
	{ label: 'Preview', id: 'preview' },
	{ label: 'Send', id: 'send' },
]

const useCycleManualEmailForm = () =>
	useForm({
		defaultValues: {
			emailTemplate: undefined as { id: string; label: string } | undefined,
			population: {
				roles: [] as { id: string; label: string }[],
				whitelistedUsers: [] as { id: string; label: string }[],
				blacklistedUsers: [] as { id: string; label: string }[],
			},
			taskFilters: {
				taskStatuses: [] as string[],
				stepIds: [] as string[],
			},
			stepValidationError: undefined as string | undefined,
		},
	})

const EmailTemplateStep = ({ visible, organizationId }: { visible: boolean; organizationId: string }) => {
	const [search, setSearch] = useState('')
	const [currentPage, setCurrentPage] = useState(1)
	const itemsPerPage = 10
	const { watch, setValue } = useFormContext()
	const { data, isLoading } = useQuery(
		trpc.admin.emailTemplates.search.queryOptions({
			organizationId,
			search,
			page: currentPage,
			perPage: itemsPerPage,
		}),
	)

	const selectedTemplate = watch('emailTemplate') as { id: string; label: string } | undefined

	useEffect(() => {
		if (!visible) return
		if (!selectedTemplate) {
			setValue('stepValidationError', 'Please select an email template')
		} else {
			setValue('stepValidationError', undefined)
		}
	}, [visible, selectedTemplate, setValue])

	if (!visible) {
		return null
	}

	return (
		<div className='flex flex-col gap-4 p-8'>
			<EditableTagField
				label='Email Template'
				maxTags={1}
				onAddTag={() => null}
				value={selectedTemplate ? [selectedTemplate] : []}
				onChangeText={(text) => {
					setSearch(text)
					setCurrentPage(1)
				}}
				onChange={(value) => {
					setValue('emailTemplate', value[0])
				}}
				placeholder='Type to search for an email template'
			/>

			{isLoading ? (
				<div className='flex justify-center py-8'>
					<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-800' />
				</div>
			) : data?.data?.length ? (
				<>
					<Table
						columns={[
							{
								field: 'id',
								label: '',
								render: (item) => (
									<Radio checked={selectedTemplate?.id === item.id} onChange={() => {}} value={item.id} />
								),
								width: '50px',
							},
							{
								field: 'name',
								label: 'Name',
							},
							{
								field: 'subject',
								label: 'Subject',
							},
							{
								field: 'tags',
								label: 'Tags',
								render: (item) => (
									<div className='flex flex-row gap-2'>
										{item.tags?.map((tag, _index) => (
											<span key={tag} className='rounded-sm bg-neutral-100 px-2 py-1 text-sm'>
												{tag}
											</span>
										))}
									</div>
								),
							},
						]}
						data={data?.data ?? []}
						className='w-full'
						noBorder
						onRowClick={(item) => {
							setValue('emailTemplate', { id: item.id, label: item.name })
							setValue('stepValidationError', undefined)
						}}
					/>
					{data?.data && data.data.length > 0 && (
						<Pagination
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							itemsPerPage={itemsPerPage}
							totalItems={data.meta.total}
						/>
					)}
				</>
			) : (
				<EmptyState title='No email templates found' />
			)}
		</div>
	)
}

const RecipientsStep = ({
	visible,
	cycleId,
	organizationId,
}: {
	visible: boolean
	cycleId: string
	organizationId: string
}) => {
	const { watch, setValue } = useFormContext()
	const [recipientsPage, setRecipientsPage] = useState(1)
	const [excludedPage, setExcludedPage] = useState(1)
	const itemsPerPage = 10

	const population = watch('population')
	const taskFilters = watch('taskFilters')
	const activeTaskStatuses = taskFilters.taskStatuses as string[]
	const activeStepIds = taskFilters.stepIds as string[]

	const [rolesSearch, setRolesSearch] = useState('')
	const [includeUserSearch, setIncludeUserSearch] = useState('')
	const [excludeUserSearch, setExcludeUserSearch] = useState('')
	const [showRecipients, setShowRecipients] = useState(false)

	const { data: rolesData } = useQuery({
		...trpc.admin.cycles.roles.list.queryOptions({ cycleId, search: rolesSearch, perPage: 50 }),
		placeholderData: keepPreviousData,
	})
	const roleOptions = useMemo(
		() =>
			(rolesData?.data ?? [])
				.filter((r, i, arr) => arr.findIndex((x) => x.roleId === r.roleId) === i)
				.map((r) => ({ value: r.roleId as string, label: r.displayName })),
		[rolesData],
	)

	const { data: includeUsersData } = useQuery({
		...trpc.admin.users.search.queryOptions({
			organizationId,
			search: includeUserSearch,
			excludeCustomFields: true,
			perPage: 50,
		}),
		placeholderData: keepPreviousData,
	})
	const includeUserOptions = useMemo(
		() => (includeUsersData?.data ?? []).map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` })),
		[includeUsersData],
	)

	// Exclude users — independent server-side search
	const { data: excludeUsersData } = useQuery({
		...trpc.admin.users.search.queryOptions({
			organizationId,
			search: excludeUserSearch,
			excludeCustomFields: true,
			perPage: 50,
		}),
		placeholderData: keepPreviousData,
	})
	const excludeUserOptions = useMemo(
		() => (excludeUsersData?.data ?? []).map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` })),
		[excludeUsersData],
	)

	// Cycle steps for step filter pills
	const { data: cycleStepsData } = useQuery(trpc.admin.cycles.steps.list.queryOptions({ cycleId }))
	const cycleStepOptions = useMemo(
		() => (cycleStepsData ?? []).map((s) => ({ id: s.id, label: s.name })),
		[cycleStepsData],
	)

	// Recipients query — keepPreviousData prevents empty flash during refetch when filters change
	const { data, isFetching } = useQuery({
		...trpc.admin.cycles.emails.recipients.list.queryOptions({
			cycleId,
			roles: population.roles?.map((role: { id: string }) => role.id) ?? [],
			whitelistedUsers: population.whitelistedUsers?.map((user: { id: string }) => user.id) ?? [],
			blacklistedUsers: population.blacklistedUsers?.map((user: { id: string }) => user.id) ?? [],
			taskStatuses:
				activeTaskStatuses.length > 0
					? (activeTaskStatuses as ('not-started' | 'in-progress' | 'completed' | 'declined' | 'rejected')[])
					: undefined,
			stepIds: activeStepIds.length > 0 ? activeStepIds : undefined,
		}),
		placeholderData: keepPreviousData,
	})

	const recipientCount = data?.recipients?.length ?? 0

	// Reset pagination whenever the actual filter content changes. Serialise to a
	// primitive string so React's Object.is comparison fires only when IDs change,
	// not on every render (watch() returns new array references each render).
	const filtersKey = [
		(population.roles ?? []).map((r: { id: string }) => r.id).join(','),
		(population.whitelistedUsers ?? []).map((u: { id: string }) => u.id).join(','),
		(population.blacklistedUsers ?? []).map((u: { id: string }) => u.id).join(','),
		activeTaskStatuses.join(','),
		activeStepIds.join(','),
	].join('|')
	// biome-ignore lint/correctness/useExhaustiveDependencies: filtersKey is a serialised primitive trigger — effect body only resets state, not reads the dep
	useEffect(() => {
		setRecipientsPage(1)
		setExcludedPage(1)
	}, [filtersKey])

	useEffect(() => {
		if (!visible) return
		// Don't fire the validation error before the initial query resolves — the
		// step would be incorrectly blocked while the request is still in-flight.
		if (isFetching || data === undefined) return
		if (!recipientCount) {
			setValue('stepValidationError', 'No recipients found')
		} else {
			setValue('stepValidationError', undefined)
		}
	}, [visible, recipientCount, isFetching, data, setValue])

	if (!visible) {
		return null
	}

	const columns: TableColumn<CycleManualEmailRecipientList['recipients'][number]>[] = [
		{
			field: 'user',
			label: 'Name',
			render: (item) => `${item.user.firstName} ${item.user.lastName}`,
		},
		{
			field: 'userId',
			label: 'Email',
			render: (item) => item.user.email,
		},
	]

	const excludedColumns: TableColumn<CycleManualEmailRecipientList['excluded'][number]>[] = [
		{
			field: 'user',
			label: 'Name',
			render: (item) => `${item.user.firstName} ${item.user.lastName}`,
		},
		{
			field: 'userId',
			label: 'Email',
			render: (item) => item.user.email,
		},
		{
			field: 'reason',
			label: '',
			width: '40px',
			render: (item) => (
				<Tooltip>
					<TooltipTrigger asChild>
						<button type='button' className='flex items-center text-neutral-400 hover:text-neutral-600 cursor-help'>
							<QuestionIcon size={14} className='fill-current' />
						</button>
					</TooltipTrigger>
					<TooltipContent>{EXCLUSION_REASON_LABELS[item.reason] ?? 'Excluded'}</TooltipContent>
				</Tooltip>
			),
		},
	]

	const recipients = data?.recipients ?? []
	const excluded = data?.excluded ?? []

	const recipientsStart = (recipientsPage - 1) * itemsPerPage
	const paginatedRecipients = recipients.slice(recipientsStart, recipientsStart + itemsPerPage)

	const excludedStart = (excludedPage - 1) * itemsPerPage
	const paginatedExcluded = excluded.slice(excludedStart, excludedStart + itemsPerPage)

	return (
		<div className='flex flex-col gap-6 p-8'>
			{/* Count banner — click to expand/collapse recipient list */}
			<div
				className={cn(
					'rounded-xl border overflow-hidden',
					recipientCount === 0 ? 'border-error-200' : 'border-primary-100',
				)}>
				<button
					type='button'
					aria-expanded={showRecipients}
					onClick={() => setShowRecipients((v) => !v)}
					className={cn(
						'flex w-full items-center justify-between px-5 py-3 cursor-pointer',
						recipientCount === 0 ? 'bg-error-50' : 'bg-primary-50',
					)}>
					<Text variant='small' weight='semibold' className='text-neutral-600'>
						Recipients
					</Text>
					<div className='flex items-center gap-2'>
						{isFetching && <Spinner className='size-4 text-neutral-400' />}
						<Text
							variant='medium'
							weight='semibold'
							className={cn('tabular-nums', recipientCount === 0 ? 'text-error-600' : 'text-primary-600')}>
							{recipientCount} {recipientCount === 1 ? 'recipient' : 'recipients'}
						</Text>
						<svg
							aria-hidden='true'
							className={cn(
								'h-4 w-4 text-neutral-400 transition-transform duration-200',
								showRecipients ? 'rotate-180' : '',
							)}
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'
							strokeWidth={2}>
							<path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
						</svg>
					</div>
				</button>
				{showRecipients && (
					<div
						className={cn(
							'flex flex-col gap-2 p-4 border-t transition-opacity duration-150',
							recipientCount === 0 ? 'border-error-100' : 'border-primary-100',
							isFetching ? 'opacity-60 pointer-events-none' : 'opacity-100',
						)}>
						<Table columns={columns} data={paginatedRecipients} noBorder />
						<Pagination
							currentPage={recipientsPage}
							setCurrentPage={setRecipientsPage}
							itemsPerPage={itemsPerPage}
							totalItems={recipients.length}
						/>
					</div>
				)}
			</div>

			{/* Population builder card — 3 labeled rows */}
			<div className='flex flex-col rounded-xl border border-neutral-200 divide-y divide-neutral-100'>
				{/* Row 1: By role */}
				<div className='flex flex-col gap-1.5 p-4'>
					<div className='flex items-start gap-3'>
						<div className='flex w-28 shrink-0 items-center gap-2 pt-2.5'>
							<span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-600'>
								1
							</span>
							<Text variant='xsmall' weight='semibold' className='text-neutral-700'>
								By role
							</Text>
						</div>
						<div className='flex-1'>
							<Select
								label=''
								placeholder='Search roles…'
								isMulti
								searchable
								onSearch={setRolesSearch}
								options={roleOptions}
								value={population.roles.map((r: { id: string }) => r.id)}
								onChange={(ids: string[]) =>
									setValue(
										'population.roles',
										ids.map((id) => ({ id, label: roleOptions.find((o) => o.value === id)?.label ?? id })),
									)
								}
							/>
						</div>
					</div>
					<Text variant='hint' className='pl-[calc(1.25rem+0.75rem+7rem)] text-neutral-400'>
						All users with the selected role(s) in this cycle will be included.
					</Text>
				</div>

				{/* Row 2: Also include */}
				<div className='flex flex-col gap-1.5 p-4'>
					<div className='flex items-start gap-3'>
						<div className='flex w-28 shrink-0 items-center gap-2 pt-2.5'>
							<span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success-100 text-sm font-bold text-success-600'>
								+
							</span>
							<Text variant='xsmall' weight='semibold' className='text-neutral-700'>
								Also include
							</Text>
						</div>
						<div className='flex-1'>
							<Select
								label=''
								placeholder='Search users…'
								isMulti
								searchable
								onSearch={setIncludeUserSearch}
								options={includeUserOptions}
								value={population.whitelistedUsers.map((u: { id: string }) => u.id)}
								onChange={(ids: string[]) =>
									setValue(
										'population.whitelistedUsers',
										ids.map((id) => ({ id, label: includeUserOptions.find((o) => o.value === id)?.label ?? id })),
									)
								}
							/>
						</div>
					</div>
					<Text variant='hint' className='pl-[calc(1.25rem+0.75rem+7rem)] text-neutral-400'>
						Add specific users not covered by the selected roles.
					</Text>
				</div>

				{/* Row 3: But exclude */}
				<div className='flex flex-col gap-1.5 p-4'>
					<div className='flex items-start gap-3'>
						<div className='flex w-28 shrink-0 items-center gap-2 pt-2.5'>
							<span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-error-100 text-sm font-bold text-error-600'>
								−
							</span>
							<Text variant='xsmall' weight='semibold' className='text-neutral-700'>
								But exclude
							</Text>
						</div>
						<div className='flex-1'>
							<Select
								label=''
								placeholder='Search users…'
								isMulti
								searchable
								onSearch={setExcludeUserSearch}
								options={excludeUserOptions}
								value={population.blacklistedUsers.map((u: { id: string }) => u.id)}
								onChange={(ids: string[]) =>
									setValue(
										'population.blacklistedUsers',
										ids.map((id) => ({ id, label: excludeUserOptions.find((o) => o.value === id)?.label ?? id })),
									)
								}
							/>
						</div>
					</div>
					<Text variant='hint' className='pl-[calc(1.25rem+0.75rem+7rem)] text-neutral-400'>
						Remove specific users from the final list.
					</Text>
				</div>
			</div>

			{/* Task filter — collapsed by default */}
			<CollapsibleSection
				defaultOpen={false}
				title={
					<div className='flex items-center gap-2'>
						<FiltersIcon size={14} className='text-neutral-400' />
						<Text variant='xsmall' weight='semibold' className='text-neutral-500'>
							Narrow by task status or step
						</Text>
						{(activeTaskStatuses.length > 0 || activeStepIds.length > 0) && (
							<span className='inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-semibold text-white'>
								{activeTaskStatuses.length + activeStepIds.length}
							</span>
						)}
					</div>
				}>
				<div className='flex flex-col gap-3 pt-3'>
					{/* Status pills */}
					<div className='flex items-center gap-3'>
						<Text variant='xsmall' weight='semibold' className='w-14 shrink-0 text-neutral-500 uppercase tracking-wide'>
							Status
						</Text>
						<div className='flex flex-wrap gap-2'>
							{TASK_STATUS_OPTIONS.map((option) => (
								<button
									key={option.value}
									type='button'
									onClick={() => {
										const updated = activeTaskStatuses.includes(option.value)
											? activeTaskStatuses.filter((s) => s !== option.value)
											: [...activeTaskStatuses, option.value]
										setValue('taskFilters.taskStatuses', updated)
									}}
									className={cn(
										'px-3 py-1 rounded-full text-sm font-medium border transition-colors cursor-pointer',
										activeTaskStatuses.includes(option.value)
											? 'bg-primary-600 text-white border-primary-600'
											: 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-400 hover:text-primary-600',
									)}>
									{option.label}
								</button>
							))}
						</div>
					</div>

					{/* Step pills */}
					{cycleStepOptions.length > 0 && (
						<div className='flex items-center gap-3'>
							<Text
								variant='xsmall'
								weight='semibold'
								className='w-14 shrink-0 text-neutral-500 uppercase tracking-wide'>
								Step
							</Text>
							<div className='flex flex-wrap gap-2'>
								{cycleStepOptions.map((step) => (
									<button
										key={step.id}
										type='button'
										onClick={() => {
											const updated = activeStepIds.includes(step.id)
												? activeStepIds.filter((id) => id !== step.id)
												: [...activeStepIds, step.id]
											setValue('taskFilters.stepIds', updated)
										}}
										className={cn(
											'px-3 py-1 rounded-full text-sm font-medium border transition-colors cursor-pointer',
											activeStepIds.includes(step.id)
												? 'bg-primary-600 text-white border-primary-600'
												: 'bg-white text-neutral-700 border-neutral-300 hover:border-primary-400 hover:text-primary-600',
										)}>
										{step.label}
									</button>
								))}
							</div>
						</div>
					)}

					{(activeTaskStatuses.length > 0 || activeStepIds.length > 0) && (
						<button
							type='button'
							className='self-start text-xs text-neutral-400 hover:text-neutral-600'
							onClick={() => {
								setValue('taskFilters.taskStatuses', [])
								setValue('taskFilters.stepIds', [])
							}}>
							Clear filters
						</button>
					)}
				</div>
			</CollapsibleSection>

			{/* Collapsible exclusions */}
			{excluded.length > 0 && (
				<CollapsibleSection
					title={
						<Text variant='small' className='text-neutral-500'>
							{excluded.length} user{excluded.length !== 1 ? 's' : ''} auto-excluded
						</Text>
					}>
					<div className='flex flex-col gap-2 mt-2'>
						<Table columns={excludedColumns} data={paginatedExcluded} noBorder />
						<Pagination
							currentPage={excludedPage}
							setCurrentPage={setExcludedPage}
							itemsPerPage={itemsPerPage}
							totalItems={excluded.length}
						/>
					</div>
				</CollapsibleSection>
			)}
		</div>
	)
}

const PreviewStep = ({
	visible,
	organizationId,
	cycleId,
}: {
	visible: boolean
	organizationId: string
	cycleId: string
}) => {
	const { watch } = useFormContext()
	const population = watch('population')
	const emailTemplate = watch('emailTemplate')
	const taskFilters = watch('taskFilters')

	const activeTaskStatuses = taskFilters.taskStatuses as string[]
	const activeStepIds = taskFilters.stepIds as string[]

	const { data: populationData } = useQuery(
		trpc.admin.cycles.emails.recipients.list.queryOptions({
			cycleId,
			roles: population.roles?.map((role: { id: string }) => role.id) ?? [],
			whitelistedUsers: population.whitelistedUsers?.map((user: { id: string }) => user.id) ?? [],
			blacklistedUsers: population.blacklistedUsers?.map((user: { id: string }) => user.id) ?? [],
			taskStatuses:
				activeTaskStatuses.length > 0
					? (activeTaskStatuses as ('not-started' | 'in-progress' | 'completed' | 'declined' | 'rejected')[])
					: undefined,
			stepIds: activeStepIds.length > 0 ? activeStepIds : undefined,
		}),
	)

	const firstRecipient = populationData?.recipients?.[0]

	const { data: emailTemplateData } = useQuery({
		...trpc.admin.emailTemplates.evaluate.queryOptions({
			emailTemplateId: emailTemplate?.id ?? '',
			context: {
				userId: firstRecipient?.userId ?? '',
				organizationId,
				cycleId,
			},
		}),
		enabled: !!emailTemplate?.id && !!firstRecipient?.userId,
	})

	if (!visible) {
		return null
	}

	return (
		<div className='flex flex-col gap-4 p-8'>
			<Text variant='h6' weight='semibold'>
				Email Preview
			</Text>

			<div>
				<TextArea label='Subject' defaultValue={emailTemplateData?.subject} disabled rows={1} />
			</div>
			<div className='mt-4'>
				<SafeHtmlRenderer htmlContent={emailTemplateData?.body ?? ''} />
			</div>
		</div>
	)
}

const SendEmailStep = ({ visible }: { visible: boolean }) => {
	const { watch, setValue } = useFormContext()
	const [confirmText, setConfirmText] = useState('')
	const emailTemplate = watch('emailTemplate')
	const stepValidationError = watch('stepValidationError')

	if (!visible) {
		if (confirmText.length) {
			setConfirmText('')
		}

		if (stepValidationError === 'Please enter SEND to confirm') {
			setValue('stepValidationError', undefined)
		}

		return null
	}

	if (!stepValidationError && confirmText !== 'SEND') {
		setValue('stepValidationError', 'Please enter SEND to confirm')
	} else if (stepValidationError && confirmText === 'SEND') {
		setValue('stepValidationError', undefined)
	}

	return (
		<div className='flex flex-col gap-4 p-8'>
			<Text variant='h6' weight='semibold'>
				Send Email
			</Text>

			<Text variant='small' weight='regular'>
				You are about to send <b>{emailTemplate?.label ?? '-'}</b> email.
				<br />
				<br />
				If you want to continue please type the word <b>SEND</b> to confirm
			</Text>

			<Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder='Enter SEND to confirm' />
		</div>
	)
}

const CycleManualEmailForm: React.FC<CycleManualEmailFormProps> = ({
	cycleId,
	organizationId,
	onSubmit: onSubmitProp,
	loading,
}) => {
	const form = useCycleManualEmailForm()
	const [currentStep, setCurrentStep] = useState(0)
	const toast = useToast()
	const [completed, setCompleted] = useState<boolean[]>(steps.map(() => false))
	const { handleSubmit, getValues } = form

	const onSubmit = handleSubmit((data) => {
		const activeTaskStatuses = data.taskFilters.taskStatuses
		const activeStepIds = data.taskFilters.stepIds

		const formData: CycleManualEmailCreateInput = {
			cycleId,
			emailTemplateId: data.emailTemplate?.id as string,
			population: {
				roles: data.population.roles?.map((role) => role.id) ?? [],
				whitelistedUsers: data.population.whitelistedUsers?.map((reviewer) => reviewer.id) ?? [],
				blacklistedUsers: data.population.blacklistedUsers?.map((reviewer) => reviewer.id) ?? [],
			},
			taskStatuses:
				activeTaskStatuses.length > 0
					? (activeTaskStatuses as ('not-started' | 'in-progress' | 'completed' | 'declined' | 'rejected')[])
					: undefined,
			stepIds: activeStepIds.length > 0 ? activeStepIds : undefined,
		}

		onSubmitProp(formData)
	})

	const markStepCompleted = (idx: number) => {
		setCompleted((prev) => {
			const copy = [...prev]
			copy[idx] = true
			return copy
		})
		const nextStep = idx + 1
		if (nextStep < steps.length) {
			setCurrentStep(nextStep)
		}
	}

	return (
		<FormProvider {...form}>
			<form onSubmit={onSubmit}>
				<Steps
					steps={steps.map((s, i) => ({ ...s, completed: completed[i] }))}
					currentStep={currentStep}
					onStepChange={(idx) => setCurrentStep(idx)}
					clickable={completed}
				/>

				<EmailTemplateStep visible={currentStep === 0} organizationId={organizationId} />

				<RecipientsStep visible={currentStep === 1} cycleId={cycleId} organizationId={organizationId} />

				<PreviewStep visible={currentStep === 2} organizationId={organizationId} cycleId={cycleId} />

				<SendEmailStep visible={currentStep === 3} />

				<div className='flex flex-row justify-end gap-4 pb-8 pr-8'>
					<Button
						variant='secondary'
						size='small'
						onClick={(e) => {
							e.preventDefault()
							if (currentStep === 0) {
								return
							}
							setCurrentStep(currentStep - 1)
						}}
						disabled={loading || currentStep === 0}>
						Previous
					</Button>
					<Button
						size='small'
						disabled={loading}
						isLoading={loading}
						onClick={(e) => {
							e.preventDefault()
							const formData = getValues()

							if (formData.stepValidationError) {
								toast.showToast(formData.stepValidationError, 'error')
								return
							}

							if (steps[currentStep].id === 'send') {
								onSubmit()
							} else {
								markStepCompleted(currentStep)
							}
						}}>
						{steps[currentStep].id === 'send' ? 'Send' : 'Next'}
					</Button>
				</div>
			</form>
		</FormProvider>
	)
}

export const CycleSendManualEmailModal: React.FC = () => {
	const { cycleId, organizationId } = useParams<{ cycleId: string; organizationId: string }>()
	const navigate = useNavigate()
	const [showLeaveConfirm, setShowLeaveConfirm] = useState(false)

	const toast = useToast()

	const { data: cycle } = useQuery(
		trpc.admin.cycles.getById.queryOptions({
			id: cycleId as string,
		}),
	)

	const { mutate: createManualEmail, isPending: isCreating } = useMutation(
		trpc.admin.cycles.emails.send.mutationOptions({
			onSuccess: () => {
				toast.showToast('Email sent successfully', 'info')
				navigate(-1)
			},
			onError: (error) => {
				toast.showToast(error.message ?? 'Error sending email', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.emails.recipients.list.queryKey({
						cycleId: cycleId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.emails.search.queryKey(),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	if (!cycleId || !organizationId) {
		return null
	}

	const onSubmit = (data: CycleManualEmailCreateInput) => {
		createManualEmail(data)
	}

	const isNotLaunched = cycle?.status !== 'in-progress' && cycle?.status !== 'completed'

	return (
		<>
			<Modal
				isOpen
				onClose={() => setShowLeaveConfirm(true)}
				fullContent
				fullScreen
				title={'New Email'}
				bodyClassName='overflow-y-auto max-h-[90vh]'>
				{isNotLaunched && (
					<Alert
						icon={WarningIcon}
						message={
							<>
								<strong>Warning:</strong> Manual emails cannot be sent until the cycle has been launched. The cycle must
								be in &quot;In Progress&quot; or &quot;Completed&quot; status.
							</>
						}
						className='bg-yellow-50 border-yellow-400 mx-8 mt-4 w-auto [&_svg]:text-yellow-600 [&_p]:text-yellow-700'
					/>
				)}
				<CycleManualEmailForm
					cycleId={cycleId as string}
					organizationId={organizationId as string}
					onSubmit={onSubmit}
					loading={isCreating}
				/>
			</Modal>
			<Modal
				isOpen={showLeaveConfirm}
				onClose={() => setShowLeaveConfirm(false)}
				title='Discard changes?'
				buttonsAlignment='right'
				buttons={[
					{ children: 'Cancel', size: 'small', variant: 'secondary', onClick: () => setShowLeaveConfirm(false) },
					{ children: 'Leave', size: 'small', variant: 'destructive', onClick: () => navigate(-1) },
				]}>
				<div className='px-6 py-4'>
					<Text variant='small'>All progress will be lost if you leave now.</Text>
				</div>
			</Modal>
		</>
	)
}
